import { createSignal, createEffect, createMemo, createSelector, Show, For, onMount, onCleanup } from "solid-js";
import { sanitizeTooltipHtml } from '@utils/htmlSanitization';
import { Portal } from "solid-js/web";
import * as d3 from "d3";
import DropDownButton from "../../../../components/buttons/DropDownButton";
import LoadingOverlay from "../../../../components/utilities/Loading";
import RaceSettings from "../../../../components/menus/RaceSettings";
import PrestartReportMap, { type PrestartMark } from "../../../../components/maneuvers/prestart/PrestartReportMap";
import PrestartChart from "../../../../components/maneuvers/prestart/PrestartChart";
import InsightPanel from "../../../../components/insights/InsightPanel";
import { deleteData, getData, getTimezoneForDate, setupMediaContainerScaling } from "../../../../utils/global";
import { persistantStore } from "../../../../store/persistantStore";
import { user } from "../../../../store/userStore";
import { toastStore } from "../../../../store/toastStore";
import { hasProjectWritePermission } from "../../../../utils/userPermissions";
import { setSelectedGroupKeys } from "../../../../store/selectionStore";
import { sourcesStore } from "../../../../store/sourcesStore";
import { tooltip } from "../../../../store/globalStore";
import { apiEndpoints } from "@config/env";
import { logPageLoad, logActivity, getCurrentProjectDatasetIds } from "../../../../utils/logging";
import { log, debug as logDebug, error as logError } from "../../../../utils/console";
import { selectedSources as filterStoreSelectedSources } from "../../../../store/filterStore";
import { useDashboardShareRestore } from "../../../../hooks/useDashboardShareRestore";
import { buildPrestartShareState, applyPrestartShareState, applyDatasetPrestartsShareState } from "../../../../utils/reportShareState";
import {
  runSelectionBannerClear,
  runSelectionBannerCut,
} from "../../../../utils/selectionBannerActions";
import { scrollToEvidence } from "../../../../utils/scrollToEvidence";
import {
  buildStartDayTrendFindings,
  buildStartFindingsForRace,
  homeInStartBundle,
  resolveHomeBoatName,
  sortFindings,
  type Finding,
  type ShowMeAction,
  type StartViewsBundle,
} from "../../../../insights";

/** Source name cell color = default source color from sourcesStore (same as Race Summary TEAM cell). */
function getSourceColor(sourceName: string | null | undefined): string | undefined {
  if (sourceName == null || String(sourceName).trim() === "") return undefined;
  const color = sourcesStore.getSourceColor(String(sourceName));
  return color ?? undefined;
}

const { selectedClassName, selectedProjectId, selectedDate, selectedDatasetId, selectedSourceName, selectedSourceId } = persistantStore;

const VIEW_OPTIONS = ["PRESTART", "ACCELERATION", "MAX BSP", "REACH", "LEG 1"] as const;
/** Map/timeseries desc index: PRESTART=0; max accel, max bsp and reach use index 1 (pre-start through reach); leg 1 only uses index 2. */
const VIEW_INDEX_MAP = { PRESTART: 0, ACCELERATION: 1, "MAX BSP": 1, REACH: 1, "LEG 1": 2 } as const;

/** Timeseries chart keys per desc, matching prestart.py addTimeSeriesData (Basics vs Details). */
const PRESTART_TIMESERIES_CHARTS: Record<string, string[]> = {
  "0_Basics": [
    "ttk_s",
    "dtl_m",
    "bsp_kph",
    "polar_perc",
    "twa_n_deg",
    "accel_rate_mps2",
    "heel_n_deg",
    "rh_lwd_mm",
    "wing_aoa_n_deg",
  ],
  "1_Details": [
    "bsp_kph",
    "polar_perc",
    "twa_n_deg",
    "accel_rate_mps2",
    "heel_n_deg",
    "pitch_deg",
    "rh_lwd_mm",
    "rud_rake_ang_deg",
    "rud_diff_ang_deg",
    "db_rake_lwd_deg",
    "db_cant_lwd_deg",
    "db_cant_eff_lwd_deg",
    "wing_aoa_n_deg",
    "wing_camber1_n_deg",
    "wing_total_twist_deg",
    "wing_clew_position_mm",
    "jib_sheet_load_kgf",
    "jib_cunno_load_kgf",
    "jib_lead_ang_deg",
  ],
  "2_Details": [
    "bsp_kph",
    "polar_perc",
    "twa_n_deg",
    "accel_rate_mps2",
    "heel_n_deg",
    "pitch_deg",
    "rh_lwd_mm",
    "rud_rake_ang_deg",
    "rud_diff_ang_deg",
    "db_rake_lwd_deg",
    "db_cant_lwd_deg",
    "db_cant_eff_lwd_deg",
    "wing_aoa_n_deg",
    "wing_camber1_n_deg",
    "wing_total_twist_deg",
    "wing_clew_position_mm",
    "jib_sheet_load_kgf",
    "jib_cunno_load_kgf",
    "jib_lead_ang_deg",
  ],
};

/** Table API view param: prestart | acceleration | maxbsp | reach | leg1. */
function getTableViewKey(view: (typeof VIEW_OPTIONS)[number]): string {
  const v = String(view ?? "").trim();
  if (v === "PRESTART") return "prestart";
  if (v === "ACCELERATION") return "acceleration";
  if (v === "MAX BSP") return "maxbsp";
  if (v === "REACH") return "reach";
  if (v === "LEG 1") return "leg1";
  return "prestart";
}

/** Label shown below the table describing how rows are ranked for the current view. */
function getRankingLabel(view: (typeof VIEW_OPTIONS)[number]): string {
  const v = String(view ?? "").trim();
  if (v === "PRESTART") return "* Ranked by distance sailed to mark 1 in first 10 seconds";
  if (v === "REACH") return "* Rankings determined by distance sailed in first 25 seconds.";
  if (v === "LEG 1") return "* Rankings determined by position after 35 seconds from the start.";
  if (v === "ACCELERATION") return "* Ranked by max acceleration";
  if (v === "MAX BSP") return "* Ranked by max boat speed.";
  return "* Ranked by time to cross line perpendicular to course axis following mark 1";
}

/** Column name for team/source in table rows; API may return TEAM or source_name (casing can vary by driver). */
const TEAM_COLUMN_NAMES = ["TEAM", "source_name"] as const;
const TEAM_COLUMN_KEYS = new Set(TEAM_COLUMN_NAMES.map((k) => k.toLowerCase()));
const isTeamColumnName = (col: string) => TEAM_COLUMN_KEYS.has(col.toLowerCase());

/** Get value from row by column key; tries exact key then case-insensitive match so API/driver casing (e.g. Twa_start vs twa_start) does not leave cells empty. */
function getRowValueByKey(row: Record<string, unknown>, col: string): unknown {
  if (Object.prototype.hasOwnProperty.call(row, col)) return row[col];
  const lower = col.toLowerCase();
  const key = Object.keys(row).find((k) => k.toLowerCase() === lower);
  return key != null ? row[key] : undefined;
}

/** Get team/source name from a table row (accept TEAM or source_name, any casing). */
function getTeamFromRow(row: Record<string, unknown>): string {
  for (const key of TEAM_COLUMN_NAMES) {
    const v = getRowValueByKey(row, key);
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

type PrestartMapBspRow = {
  sourceName: string;
  color: string;
  bspDisplay: string;
  dimmed: boolean;
};

function readChannelValue(rec: Record<string, unknown>, channelKey: string): number | null {
  const keyLower = channelKey.toLowerCase();
  let raw: unknown = rec[channelKey];
  if (raw == null) {
    const match = Object.keys(rec).find((k) => k.toLowerCase() === keyLower);
    if (match) {
      raw = rec[match];
    }
  }
  const value = typeof raw === "number" ? raw : raw != null ? Number(raw) : NaN;
  return Number.isFinite(value) ? value : null;
}

/**
 * Boat speed at timeSec (nearest sample). With no selected time, use the last sample,
 * which is where the map boat sits before the timeseries is scrubbed.
 */
function bspAtTime(values: Record<string, number>[], timeSec: number | null): number | null {
  let best: number | null = null;
  let bestDist = Infinity;
  let last: number | null = null;
  for (const row of values) {
    const rec = row as Record<string, unknown>;
    const value = readChannelValue(rec, "bsp_kph");
    if (value == null) {
      continue;
    }
    last = value;
    if (timeSec == null || !Number.isFinite(timeSec)) {
      continue;
    }
    const rawTime = rec.time;
    const time = typeof rawTime === "number" && Number.isFinite(rawTime) ? rawTime : null;
    if (time == null) {
      continue;
    }
    const dist = Math.abs(time - timeSec);
    if (dist < bestDist) {
      bestDist = dist;
      best = value;
    }
  }
  if (timeSec == null || !Number.isFinite(timeSec)) {
    return last;
  }
  return best;
}

function formatBspKph(value: number | null): string {
  if (value == null || !Number.isFinite(value)) {
    return "—";
  }
  return (Math.round(value * 10) / 10).toFixed(1);
}

function formatPrestartSliderTime(timeSec: number): string {
  const rounded = Math.round(timeSec * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  if (rounded === 0) {
    return "0s";
  }
  return rounded > 0 ? `${text}s` : `−${text.replace("-", "")}s`;
}

/** Get the ranking column key for a given view. */
function getRankingColumnKey(view: (typeof VIEW_OPTIONS)[number]): string {
  const v = String(view ?? "").trim();
  if (v === "PRESTART") return "Prestart_dist";
  if (v === "ACCELERATION") return "Accel_max";
  if (v === "MAX BSP") return "Bsp_max";
  if (v === "REACH") return "Reach_dist";
  if (v === "LEG 1") return "Leg1_dist";
  return "Prestart_dist";
}

/** Determine if ranking should be ascending (true for position/time-based, false for performance-based). */
function isRankingAscending(view: (typeof VIEW_OPTIONS)[number]): boolean {
  const v = String(view ?? "").trim();
  // REACH and LEG 1: higher distance is better (rank descending)
  if (v === "REACH" || v === "LEG 1") return false;
  // Performance-based views: higher is better (descending)
  return false;
}

/** Recalculate RANK column for filtered rows based on the current view. */
function recalculateRankings(rows: Record<string, unknown>[], currentView: (typeof VIEW_OPTIONS)[number]): Record<string, unknown>[] {
  if (rows.length === 0) return rows;

  const rankingColumn = getRankingColumnKey(currentView);
  const ascending = isRankingAscending(currentView);

  // Sort rows by the ranking column
  const sorted = [...rows].sort((a, b) => {
    const aVal = getRowValueByKey(a, rankingColumn);
    const bVal = getRowValueByKey(b, rankingColumn);
    let aNum = typeof aVal === "number" ? aVal : Number(aVal);
    let bNum = typeof bVal === "number" ? bVal : Number(bVal);
    // Treat -999 (sentinel for no data) as sort last
    if (aNum === -999) aNum = ascending ? Infinity : -Infinity;
    if (bNum === -999) bNum = ascending ? Infinity : -Infinity;

    // Handle null/NaN values (put them at the end)
    if (!Number.isFinite(aNum) && !Number.isFinite(bNum)) return 0;
    if (!Number.isFinite(aNum)) return 1;
    if (!Number.isFinite(bNum)) return -1;

    return ascending ? aNum - bNum : bNum - aNum;
  });

  // Assign new ranks (1-based)
  sorted.forEach((row, index) => {
    // Find the RANK key (case-insensitive)
    const rankKey = Object.keys(row).find((k) => k.toUpperCase() === "RANK") || "RANK";
    row[rankKey] = index + 1;
  });

  return sorted;
}

/** Mark names to show on prestart map (SL1, SL2, MK1, M1). */
const PRESTART_MARK_NAMES = ["SL1", "SL2", "MK1", "M1"] as const;

/** Per-mode table config: column order, header names, and tooltips. Keys match API column names (case-insensitive match used). */
type ViewTableConfig = { columnOrder: string[]; columns: Record<string, { header: string; tooltip: string }> };

/**
 * Tooltips for columns that appear in more than one view, kept in one place so the wording
 * cannot drift between views. Descriptions follow maneuvers/prestart.py: boat speeds are
 * stored as kts * 1.852 (kph), `sec` is relative to the start gun (negative = before it),
 * `leg1_time` is when the FIRST boat crosses the M1 gate and `reach_time` is a fixed
 * fraction of it, so both are the same cut for every boat in the fleet.
 */
const SHARED_COLUMN_TIPS: Record<string, { header: string; tooltip: string }> = {
  TEAM: { header: "Team", tooltip: "Boat / source name" },
  Bsp_start: { header: "BSP start", tooltip: "Boat speed (kph) at the start" },
  Twa_start: { header: "TWA start", tooltip: "True wind angle (deg, absolute) at the start" },
  DTL_start: { header: "DTL start", tooltip: "Distance to line (m) at the start" },
  LINE_PERC_start: { header: "Line % start", tooltip: "Position along the start line (%) at the start" },
  RATIO_turnback: { header: "Ratio turnback", tooltip: "Start ratio at the turnback" },
  TTK_turnback: { header: "TTK turnback", tooltip: "Time to kill (s) at the turnback" },
  Accel_max: { header: "Accel max", tooltip: "Maximum acceleration (m/s²) from 20s before the gun to the leg 1 gate crossing" },
  Bsp_max: { header: "BSP max", tooltip: "Maximum boat speed (kph) from the turnback to the leg 1 gate crossing" },
};

const PRESTART_TABLE_CONFIG: Record<string, ViewTableConfig> = {
  prestart: {
    columnOrder: [
      "RANK",
      "TEAM",
      "Prestart_dist",
      "Bsp_start",
      "Twa_start",
      "DTL_start",
      "LINE_PERC_start",
      "RATIO_turnback",
      "TTK_turnback",
      "TTK_burn",
      "Bsp_avg_pre",
      "Time_turnback",
      "DTL_turnback",
    ],
    columns: {
      ...SHARED_COLUMN_TIPS,
      RANK: { header: "Rank", tooltip: "Position ranked by distance past the start line 10s after the gun (furthest toward mark 1 = 1)" },
      Prestart_dist: { header: "Distance [M]", tooltip: "Signed distance (m) from the start line at the last sample before 10s after the gun; negative = still short of the line, positive = past it toward mark 1" },
      TTK_burn: { header: "TTK burn", tooltip: "Time to kill (s) burned during the turnback: TTK at turnback minus TTK at turnback exit" },
      Bsp_avg_pre: { header: "BSP avg pre", tooltip: "Average boat speed (kph) from the turnback exit to the start" },
      Time_turnback: { header: "Time turnback", tooltip: "Time (s) of the turnback relative to the gun (negative = before the start)" },
      DTL_turnback: { header: "DTL turnback", tooltip: "Distance to line (m) at the turnback" },
    },
  },
  acceleration: {
    columnOrder: [
      "RANK",
      "TEAM",
      "Time_accmax",
      "Accel_max",
      "Bsp_accmax",
      "Twa_accmax",
      "Heel_accmax",
      "RH_lwd_accmax",
      "Cant_accmax",
      "Jib_sheet_load_accmax",
      "Jib_cunno_load_accmax",
      "Jib_lead_ang_accmax",
      "Wing_clew_pos_accmax",
      "Wing_twist_accmax",
      "CA1_accmax",
    ],
    columns: {
      ...SHARED_COLUMN_TIPS,
      RANK: { header: "Rank", tooltip: "Position ranked by max acceleration" },
      Time_accmax: { header: "Time at acc max", tooltip: "Time (s) of max acceleration, relative to the gun (negative = before the start)" },
      Bsp_accmax: { header: "BSP at acc max", tooltip: "Boat speed (kph) at max acceleration" },
      Twa_accmax: { header: "TWA at acc max", tooltip: "True wind angle (deg, signed) at max acceleration" },
      Heel_accmax: { header: "Heel at acc max", tooltip: "Heel angle (deg) at max acceleration" },
      RH_lwd_accmax: { header: "RH lwd at acc max", tooltip: "Leeward ride height (mm) at max acceleration" },
      Cant_accmax: { header: "Cant at acc max", tooltip: "Leeward daggerboard cant angle (deg) at max acceleration" },
      Jib_sheet_load_accmax: { header: "Jib sheet at acc max", tooltip: "Jib sheet load (kgf) at max acceleration" },
      Jib_cunno_load_accmax: { header: "Jib cunno at acc max", tooltip: "Jib cunningham load (kgf) at max acceleration" },
      Jib_lead_ang_accmax: { header: "Jib lead ang at acc max", tooltip: "Jib lead angle (deg) at max acceleration" },
      Wing_clew_pos_accmax: { header: "Wing clew at acc max", tooltip: "Wing clew position (mm) at max acceleration" },
      Wing_twist_accmax: { header: "Wing twist at acc max", tooltip: "Wing twist (deg) at max acceleration" },
      CA1_accmax: { header: "CA1 at acc max", tooltip: "CA1 angle (deg) at max acceleration" },
    },
  },
  maxbsp: {
    columnOrder: [
      "RANK",
      "TEAM",
      "Time_bspmax",
      "Bsp_max",
      "Twa_bspmax",
      "Heel_bspmax",
      "RH_lwd_bspmax",
      "Cant_bspmax",
      "Jib_sheet_load_bspmax",
      "Jib_cunno_load_bspmax",
      "Jib_lead_ang_bspmax",
      "Wing_clew_pos_bspmax",
      "Wing_twist_bspmax",
      "CA1_bspmax",
    ],
    columns: {
      ...SHARED_COLUMN_TIPS,
      RANK: { header: "Rank", tooltip: "Position ranked by max boat speed" },
      Time_bspmax: { header: "Time at BSP max", tooltip: "Time (s) of max boat speed, relative to the gun (negative = before the start)" },
      Twa_bspmax: { header: "TWA at BSP max", tooltip: "True wind angle (deg, signed) at max boat speed" },
      Heel_bspmax: { header: "Heel at BSP max", tooltip: "Heel angle (deg) at max boat speed" },
      RH_lwd_bspmax: { header: "RH lwd at BSP max", tooltip: "Leeward ride height (mm) at max boat speed" },
      Cant_bspmax: { header: "Cant at BSP max", tooltip: "Leeward daggerboard cant angle (deg) at max boat speed" },
      Jib_sheet_load_bspmax: { header: "Jib sheet at BSP max", tooltip: "Jib sheet load (kgf) at max boat speed" },
      Jib_cunno_load_bspmax: { header: "Jib cunno at BSP max", tooltip: "Jib cunningham load (kgf) at max boat speed" },
      Jib_lead_ang_bspmax: { header: "Jib lead ang at BSP max", tooltip: "Jib lead angle (deg) at max boat speed" },
      Wing_clew_pos_bspmax: { header: "Wing clew at BSP max", tooltip: "Wing clew position (mm) at max boat speed" },
      Wing_twist_bspmax: { header: "Wing twist at BSP max", tooltip: "Wing twist (deg) at max boat speed" },
      CA1_bspmax: { header: "CA1 at BSP max", tooltip: "CA1 angle (deg) at max boat speed" },
    },
  },
  reach: {
    columnOrder: [
      "RANK",
      "TEAM",
      "Reach_dist",
      "Bsp_avg_reach",
      "TTK_turnback",
      "RATIO_turnback",
      "DTL_start",
      "LINE_PERC_start",
      "Bsp_start",
      "Twa_start",
      "Accel_max",
      "Bsp_max",
    ],
    columns: {
      ...SHARED_COLUMN_TIPS,
      RANK: { header: "Rank", tooltip: "Position ranked by reach distance (max distance = 1)" },
      Reach_dist: { header: "Reach dist [m]", tooltip: "Distance (m) from the start line to the boat at the reach time cut (a fixed fraction of the leg 1 time, so the same cut for every boat)" },
      Bsp_avg_reach: { header: "BSP avg reach", tooltip: "Average boat speed (kph) from the gun to the reach time cut" },
    },
  },
  leg1: {
    columnOrder: [
      "RANK",
      "TEAM",
      "Leg1_dist",
      "TTK_turnback",
      "RATIO_turnback",
      "DTL_start",
      "LINE_PERC_start",
      "Bsp_start",
      "Twa_start",
      "Accel_max",
      "Bsp_max",
    ],
    columns: {
      ...SHARED_COLUMN_TIPS,
      RANK: { header: "Rank", tooltip: "Position by leg1 distance (max distance = 1)" },
      Leg1_dist: { header: "Leg1 dist [m]", tooltip: "Distance (m) from the start line to the boat when the FIRST boat crossed the M1 gate (the same cut for every boat)" },
    },
  },
};

/** Resolve actual column key from row (case-insensitive). */
function resolveColumnKey(rowKeys: string[], configKey: string): string | null {
  const lower = configKey.toLowerCase();
  return rowKeys.find((k) => k.toLowerCase() === lower) ?? null;
}

/** Get header name and tooltip for a column key in the given view. Falls back to key with underscores replaced by spaces. */
function getColumnDisplay(
  viewKey: string,
  colKey: string
): { header: string; tooltip: string } {
  if (colKey.toLowerCase() === START_COLUMN.toLowerCase()) {
    return { header: "Start", tooltip: "Gun time for this prestart" };
  }
  const config = PRESTART_TABLE_CONFIG[viewKey];
  if (config?.columns) {
    const lower = colKey.toLowerCase();
    const entry = Object.entries(config.columns).find(([k]) => k.toLowerCase() === lower);
    if (entry) return entry[1];
  }
  return { header: colKey.replace(/_/g, " "), tooltip: colKey.replace(/_/g, " ") };
}

function dateNorm(): string {
  const d = selectedDate();
  if (!d || typeof d !== "string") return "";
  return String(d).replace(/[-/]/g, "");
}

/** Format date for marks/markwind API (YYYY-MM-DD). Matches RaceCourseLayer formatDateForAPI. */
function formatDateForMarksApi(dateNorm: string): string {
  if (!dateNorm || dateNorm.length !== 8) return dateNorm;
  return `${dateNorm.slice(0, 4)}-${dateNorm.slice(4, 6)}-${dateNorm.slice(6, 8)}`;
}

/** Dataset report row identity: gun time, instead of the fleet TEAM column. */
const START_COLUMN = "START";

/** Distinct series colors when every row is the same source. */
const SERIES_COLORS = [
  "#4C78A8",
  "#F58518",
  "#54A24B",
  "#E45756",
  "#72B7B2",
  "#EECA3B",
  "#B279A2",
  "#FF9DA6",
  "#9D755D",
  "#BAB0AC",
];

/** Parse a race start time (ISO string or timestamp) to ms for comparison. */
function parseTimeToMs(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v === "number" && Number.isFinite(v)) return v < 1e13 ? v * 1000 : v;
  const s = String(v).trim();
  if (!s) return null;
  const n = Number(s);
  if (!Number.isNaN(n) && Number.isFinite(n)) return n < 1e13 ? n * 1000 : n;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

/** Gun time in the day's timezone, HH:mm:ss. */
function formatStartLabel(raw: unknown, timeZone: string | null): string {
  const ms = parseTimeToMs(raw);
  if (ms == null) return "";
  const options: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  };
  if (timeZone) options.timeZone = timeZone;
  try {
    return new Intl.DateTimeFormat("en-GB", options).format(new Date(ms));
  } catch {
    return "";
  }
}

/** Keep identical clock times distinguishable by appending the race number. */
function disambiguateStartLabels(entries: { label: string; race: string }[]): string[] {
  const counts = new Map<string, number>();
  for (const entry of entries) {
    counts.set(entry.label, (counts.get(entry.label) ?? 0) + 1);
  }
  return entries.map((entry) => {
    const base = entry.label || (entry.race ? `Race ${entry.race}` : "Start");
    if ((counts.get(entry.label) ?? 0) > 1 && entry.race) {
      return `${base} R${entry.race}`;
    }
    return base;
  });
}

export type PrestartPageMode = "fleet" | "dataset";

export default function PrestartPage(props: { mode?: PrestartPageMode }) {
  const isDatasetMode = () => props.mode === "dataset";
  const [loading, setLoading] = createSignal(false);
  const [races, setRaces] = createSignal<string[]>([]);
  const [selectedRace, setSelectedRace] = createSignal<string>("");
  const [view, setView] = createSignal<typeof VIEW_OPTIONS[number]>("PRESTART");
  const [tableRows, setTableRows] = createSignal<Record<string, unknown>[]>([]);
  const [tableColumns, setTableColumns] = createSignal<string[]>([]);
  const [sortConfig, setSortConfig] = createSignal<{ key: string; direction: "asc" | "desc" }>({ key: "", direction: "asc" });
  const [columnScales, setColumnScales] = createSignal<Record<string, string>>({});
  const [formattedColumns, setFormattedColumns] = createSignal<string[]>([]);
  const [isHoveredTable, setIsHoveredTable] = createSignal(false);
  const [showCopyTable, setShowCopyTable] = createSignal(false);
  const [copySuccess, setCopySuccess] = createSignal(false);
  const [mapData, setMapData] = createSignal<{ event_id: number; source_name: string; points: { lat: number; lng: number; time?: number; hdg?: number; note?: string }[]; marks?: PrestartMark[] }[]>([]);
  /** All mark sets for the day: array of { datetime (ISO), marks } from markwind/marks object. */
  const [marksByTime, setMarksByTime] = createSignal<{ datetime: string; marks: PrestartMark[] }[]>([]);
  const [tsData, setTsData] = createSignal<{ event_id: number; source_name: string; charts: string[]; values: Record<string, number>[] }[]>([]);
  /** Course axis for map when table view doesn't return it; fetched from courseaxis view. */
  const [courseAxisFromApi, setCourseAxisFromApi] = createSignal<number | undefined>(undefined);
  /** Selected time (sec, race-relative) from timeseries hover or click; drives red line, value labels, map trim, and the map BSP table. Local to prestart page only. */
  const [selectedTimeSec, setSelectedTimeSec] = createSignal<number | null>(null);
  /** Insight engine: multi-view start_stats for the selected race (+ optional day trends). */
  const [insightFindings, setInsightFindings] = createSignal<Finding[]>([]);
  const [insightFleetSize, setInsightFleetSize] = createSignal(0);
  /** Set when the home boat has no row in the selected race, so other races' cards stay hidden. */
  const [insightStatus, setInsightStatus] = createSignal('');
  /** Timezone for the selected day, used to label dataset prestarts. */
  const [dayTimezone, setDayTimezone] = createSignal<string | null>(null);
  /**
   * Dataset mode clears selectedDate. The report date and source come from the dataset row.
   */
  const [datasetReportDate, setDatasetReportDate] = createSignal("");
  const [datasetReportSource, setDatasetReportSource] = createSignal("");
  const [datasetInfoLoaded, setDatasetInfoLoaded] = createSignal(false);
  /**
   * Pending Show-me team selection. `fetchTable` clears row selection on every view/race fetch;
   * we stash the teams here, clear immediately, then re-apply after the fetch completes.
   */
  let pendingShowMeTeams: string[] | null = null;
  /** Time from a share link. The table fetch must not clear it before the shared cursor is applied. */
  let pendingShareTimeSec: number | null | undefined;
  /** Scroll to table/map after loading remounts the evidence row (view change unmounts it). */
  let pendingScrollToEvidence = false;
  /** Drops table responses from an older view or race list. */
  let tableFetchGeneration = 0;

  const viewIndex = () => VIEW_INDEX_MAP[view()];
  /**
   * Map data trim:
   * - PRESTART: untrimmed.
   * - REACH, ACCELERATION, MAX BSP, LEG 1: same as average leg 1 — 60 seconds, extended if needed so max BSP / max accel markers are visible.
   */
  const mapDataForView = createMemo(() => {
    const tracks = mapData();
    const currentView = view();
    if (currentView === "PRESTART") return tracks;
    // REACH, ACCELERATION, MAX BSP, LEG 1 all use leg1 map and same time range (average leg 1)
    const leg1Views = ["REACH", "ACCELERATION", "MAX BSP", "LEG 1"];
    const trimTime = leg1Views.includes(currentView) ? 60 : Infinity;
    if (!Number.isFinite(trimTime) || tracks.length === 0) return tracks;
    let finalTrim = trimTime;
    // Extend trim so map data includes points with note 'max bsp' / 'max accel' for star + golden boat markers
    const markerTimes = tracks.flatMap((track) =>
      track.points
        .filter((p) => (p.note === "max bsp" || p.note === "max accel") && typeof p.time === "number" && Number.isFinite(p.time))
        .map((p) => p.time as number)
    );
    finalTrim = markerTimes.length > 0 ? Math.max(trimTime, ...markerTimes) : trimTime;
    return tracks.map((track) => ({
      ...track,
      points: track.points.filter((p) => p.time == null || p.time <= finalTrim),
    }));
  });
  /** Timeseries data: PRESTART untrimmed; REACH, ACCELERATION, MAX BSP, LEG 1 use same time range as average leg 1 (60s). */
  const tsDataForView = createMemo(() => {
    const data = tsData();
    const currentView = view();
    if (currentView === "PRESTART") return data;
    const leg1Views = ["REACH", "ACCELERATION", "MAX BSP", "LEG 1"];
    const minTime = leg1Views.includes(currentView) ? 60 : Infinity;
    if (!Number.isFinite(minTime) || data.length === 0) return data;
    return data.map((row) => ({
      ...row,
      values: (row.values || []).filter((v) => {
        const t = (v as Record<string, unknown>).time;
        const time = typeof t === "number" && Number.isFinite(t) ? t : null;
        return time == null || time <= minTime;
      }),
    }));
  });

  const eventIds = createMemo(() =>
    tableRows()
      .map((r) => (r.event_id != null ? Number(r.event_id) : null))
      .filter((id): id is number => id != null && !Number.isNaN(id))
  );
  /** Source-of-truth for prestart selection (source_name). Synced to selectionStore as selectedGroupKeys. */
  const [selectedSourceNamesSignal, setSelectedSourceNamesSignal] = createSignal<string[]>([]);
  const selectedSourceNames = () => selectedSourceNamesSignal();

  /** Time span shared with the timeseries x-axis, so the map slider scrubs the same clock. */
  const mapTimeExtent = createMemo((): [number, number] | null => {
    const times: number[] = [];
    for (const row of tsDataForView()) {
      for (const value of row.values || []) {
        const raw = (value as Record<string, unknown>).time;
        if (typeof raw === "number" && Number.isFinite(raw)) {
          times.push(raw);
        }
      }
    }
    if (times.length === 0) {
      for (const track of mapDataForView()) {
        for (const point of track.points) {
          if (typeof point.time === "number" && Number.isFinite(point.time)) {
            times.push(point.time);
          }
        }
      }
    }
    if (times.length === 0) {
      return null;
    }
    const min = Math.min(...times);
    const max = Math.max(...times);
    if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
      return null;
    }
    return [min, max];
  });

  /** Fleet rows are boats. Dataset rows are start times, and that label is the series key. */
  const seriesKeyFromRow = (row: Record<string, unknown>): string => {
    if (isDatasetMode()) {
      const start = getRowValueByKey(row, START_COLUMN);
      if (start != null && String(start).trim() !== "") return String(start).trim();
    }
    return getTeamFromRow(row);
  };

  /** Day mode uses the selected date. Dataset mode uses the dataset's own date. */
  const activeDate = (): string => {
    if (isDatasetMode()) return datasetReportDate().replace(/[-/]/g, "");
    return dateNorm();
  };

  const datasetSourceLabel = (): string => {
    const fromDataset = datasetReportSource().trim();
    if (fromDataset) return fromDataset;
    const named = String(selectedSourceName() ?? "").trim();
    if (named && named.toUpperCase() !== "ALL") return named;
    const fromId = sourcesStore.getSourceName(selectedSourceId());
    return fromId != null ? String(fromId).trim() : "";
  };

  const seriesColor = (name: string | null | undefined): string | undefined => {
    if (name == null || String(name).trim() === "") return undefined;
    if (!isDatasetMode()) return getSourceColor(name);
    const label = String(name).trim().toLowerCase();
    const ordered = [...tableRows()].sort(
      (a, b) => (parseTimeToMs(a.race_start_time) ?? 0) - (parseTimeToMs(b.race_start_time) ?? 0)
    );
    const idx = ordered.findIndex(
      (row) => String(getRowValueByKey(row, START_COLUMN) ?? "").trim().toLowerCase() === label
    );
    return SERIES_COLORS[(idx < 0 ? 0 : idx) % SERIES_COLORS.length];
  };

  const pageLogName = () => (isDatasetMode() ? "Prestarts" : "Start Summary");

  /** Source name + boat speed (kph) at the selected time, in summary-table order. */
  const mapBspRows = createMemo((): PrestartMapBspRow[] => {
    const series = tsDataForView();
    const time = selectedTimeSec();
    const selected = selectedSourceNamesSignal();
    const hasSelection = selected.length > 0;
    const rank = tableRows().map((row) => seriesKeyFromRow(row)).filter((name) => name !== "");
    const rows: PrestartMapBspRow[] = series.map((row) => {
      const bsp = bspAtTime(row.values || [], time);
      return {
        sourceName: row.source_name,
        color: seriesColor(row.source_name) ?? "#888",
        bspDisplay: formatBspKph(bsp),
        dimmed: hasSelection && !selected.includes(row.source_name),
      };
    });
    if (rank.length === 0) return rows;
    return [...rows].sort((a, b) => {
      const ia = rank.indexOf(a.sourceName);
      const ib = rank.indexOf(b.sourceName);
      return (ia < 0 ? rank.length : ia) - (ib < 0 ? rank.length : ib);
    });
  });

  const sharePageKey = props.mode === "dataset" ? "DatasetPrestarts" : "Prestart";
  useDashboardShareRestore(
    sharePageKey,
    () =>
      buildPrestartShareState({
        selectedRace: isDatasetMode() ? "" : selectedRace(),
        view: view(),
        selectedTimeSec: selectedTimeSec(),
        selectedSourceNames: selectedSourceNamesSignal(),
        sortConfig: sortConfig(),
        columnScales: columnScales(),
        formattedColumns: formattedColumns(),
      }),
    (obj) => {
      const setters = {
        setView,
        setSelectedTimeSec,
        setSelectedSourceNames: setSelectedSourceNamesSignal,
        setSortConfig,
        setColumnScales,
        setFormattedColumns,
      };
      if (isDatasetMode()) {
        const applied = applyDatasetPrestartsShareState(obj, sharePageKey, setters);
        if (applied && Array.isArray(obj.selectedSourceNames) && obj.selectedSourceNames.length > 0) {
          pendingShowMeTeams = (obj.selectedSourceNames as unknown[]).map((name) => String(name));
        }
        if (applied && (obj.selectedTimeSec === null || typeof obj.selectedTimeSec === "number")) {
          pendingShareTimeSec = obj.selectedTimeSec as number | null;
        }
        return applied;
      }
      const applied = applyPrestartShareState(obj, sharePageKey, {
        ...setters,
        setSelectedRace,
      });
      if (applied && Array.isArray(obj.selectedSourceNames) && obj.selectedSourceNames.length > 0) {
        pendingShowMeTeams = (obj.selectedSourceNames as unknown[]).map((name) => String(name));
      }
      if (applied && (obj.selectedTimeSec === null || typeof obj.selectedTimeSec === "number")) {
        pendingShareTimeSec = obj.selectedTimeSec as number | null;
      }
      return applied;
    }
  );

  /** Get selected source names from filterStore (same as FleetMap uses) */
  const getSelectedSourceNames = (): Set<string> | null => {
    try {
      const sourceNames = filterStoreSelectedSources();
      if (Array.isArray(sourceNames) && sourceNames.length > 0) {
        // Normalize to lowercase for case-insensitive matching
        return new Set(sourceNames.map((name: string) => String(name).toLowerCase().trim()));
      }
      return null;
    } catch (err) {
      logError('Prestart: Error getting selected source names', err);
      return null;
    }
  };

  const fetchRaces = async () => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const date = activeDate();
    if (!className || !projectId || date.length !== 8) {
      setRaces([]);
      return;
    }
    try {
      const timezone = await getTimezoneForDate(className, Number(projectId), date);
      setDayTimezone(timezone);
      let url = `${apiEndpoints.app.datasets}/date/races?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&date=${encodeURIComponent(date)}&from_race_events=1`;
      if (isDatasetMode()) {
        const datasetId = selectedDatasetId();
        if (!datasetId) {
          setRaces([]);
          return;
        }
        url += `&dataset_id=${encodeURIComponent(String(datasetId))}`;
      }
      if (timezone) url += `&timezone=${encodeURIComponent(timezone)}`;
      const result = await getData(url);
      if (!result?.success || !Array.isArray(result.data)) {
        setRaces([]);
        return;
      }
      const raceKeys = (result.data as { Race_number?: number }[])
        .map((r) => (r?.Race_number != null ? String(r.Race_number) : null))
        .filter((k): k is string => k != null && k !== "" && k !== "-1");
      setRaces(raceKeys);
      if (raceKeys.length > 0 && !selectedRace() && !isDatasetMode()) setSelectedRace(raceKeys[0]);
    } catch (err) {
      logError("Prestart: fetch races failed", err);
      setRaces([]);
    }
  };

  const isCurrentTableFetch = (generation: number): boolean => generation === tableFetchGeneration;

  const columnsFromRows = (rows: Record<string, unknown>[]): string[] => {
    if (rows.length === 0) return [];
    const raw = Object.keys(rows[0]).filter((k) => {
      if (k === "event_id" || k === "Course_axis" || k === "course_axis" || k === "race_start_time" || k === "race_key") return false;
      if (isDatasetMode() && (isTeamColumnName(k) || k.toUpperCase() === "RANK")) return false;
      return true;
    });
    const viewKey = getTableViewKey(view());
    const config = PRESTART_TABLE_CONFIG[viewKey];
    if (config?.columnOrder?.length) {
      const ordered: string[] = [];
      for (const configKey of config.columnOrder) {
        const key = isDatasetMode() && configKey.toUpperCase() === "TEAM" ? START_COLUMN : configKey;
        const resolved = resolveColumnKey(raw, key);
        if (resolved) ordered.push(resolved);
      }
      const remaining = raw
        .filter((k) => !ordered.some((o) => o.toLowerCase() === k.toLowerCase()))
        .sort((a, b) => a.localeCompare(b));
      return [...ordered, ...remaining];
    }
    const rankKey = raw.find((k) => k.toUpperCase() === "RANK");
    const identityKey = raw.find((k) =>
      isDatasetMode()
        ? k.toLowerCase() === START_COLUMN.toLowerCase()
        : TEAM_COLUMN_NAMES.some((t) => t.toLowerCase() === k.toLowerCase())
    );
    const rest = raw.filter((k) => k !== rankKey && k !== identityKey).sort((a, b) => a.localeCompare(b));
    return [...(rankKey ? [rankKey] : []), ...(identityKey ? [identityKey] : []), ...rest];
  };

  const fetchTable = async (generation: number) => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const date = activeDate();
    const race = selectedRace();
    if (!className || !projectId || date.length !== 8 || !race) {
      setTableRows([]);
      setTableColumns([]);
      setCourseAxisFromApi(undefined);
      return;
    }
    setCourseAxisFromApi(undefined);
    try {
      const url = `${apiEndpoints.app.data}/prestart-summary?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&date=${encodeURIComponent(date)}&race=${encodeURIComponent(race)}&view=${getTableViewKey(view())}`;
      const result = await getData(url);
      if (!isCurrentTableFetch(generation)) return;
      if (!result?.success || !result?.data?.rows) {
        setTableRows([]);
        setTableColumns([]);
        applyPendingOrClearSelection([]);
        setCourseAxisFromApi(undefined);
        return;
      }
      let rows = result.data.rows as Record<string, unknown>[];

      // Filter rows by global source selection from filterStore (same as FleetMap)
      const selectedSourceNames = getSelectedSourceNames();
      if (selectedSourceNames) {
        rows = rows.filter((row) => {
          const sourceName = getTeamFromRow(row as Record<string, unknown>);
          return sourceName && selectedSourceNames.has(sourceName.toLowerCase().trim());
        });
      }

      // Recalculate rankings after filtering
      rows = recalculateRankings(rows, view());

      if (!isCurrentTableFetch(generation)) return;
      setTableRows(rows);
      if (rows.length > 0) {
        setTableColumns(columnsFromRows(rows));
        const first = rows[0];
        const fromRow = first?.Course_axis ?? (first as Record<string, unknown>)?.course_axis;
        const numFromRow = typeof fromRow === "number" && Number.isFinite(fromRow) ? fromRow : null;
        if (numFromRow == null) {
          try {
            const axisUrl = `${apiEndpoints.app.data}/prestart-summary?class_name=${encodeURIComponent(className!)}&project_id=${encodeURIComponent(projectId!)}&date=${encodeURIComponent(date)}&race=${encodeURIComponent(race!)}&view=courseaxis`;
            const axisResult = await getData(axisUrl);
            if (!isCurrentTableFetch(generation)) return;
            const axisRows = axisResult?.success && axisResult?.data?.rows ? (axisResult.data.rows as Record<string, unknown>[]) : [];
            const axisVal = axisRows[0]?.Course_axis ?? (axisRows[0] as Record<string, unknown>)?.course_axis;
            setCourseAxisFromApi(typeof axisVal === "number" && Number.isFinite(axisVal) ? axisVal : undefined);
          } catch {
            if (isCurrentTableFetch(generation)) setCourseAxisFromApi(undefined);
          }
        } else {
          setCourseAxisFromApi(undefined);
        }
        if (!isCurrentTableFetch(generation)) return;
        applyPendingOrClearSelection(rows);
      } else {
        setTableColumns([]);
        applyPendingOrClearSelection([]);
        setCourseAxisFromApi(undefined);
      }
    } catch (err) {
      logError("Prestart: fetch table failed", err);
      if (!isCurrentTableFetch(generation)) return;
      setTableRows([]);
      setTableColumns([]);
      applyPendingOrClearSelection([]);
      setCourseAxisFromApi(undefined);
    }
  };

  /** One row per prestart for this source, ordered by gun time. */
  const fetchDatasetTable = async (generation: number) => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const date = activeDate();
    const raceKeys = races();
    const sourceLabel = datasetSourceLabel();
    const sourceKey = sourceLabel.toLowerCase();
    if (!className || !projectId || date.length !== 8 || raceKeys.length === 0 || !sourceKey) {
      if (!isCurrentTableFetch(generation)) return;
      setTableRows([]);
      setTableColumns([]);
      setCourseAxisFromApi(undefined);
      if (!sourceKey) {
        logDebug("Prestarts: no source name for dataset", { datasetId: selectedDatasetId() });
      }
      return;
    }
    setCourseAxisFromApi(undefined);
    try {
      const viewKey = getTableViewKey(view());
      const tz = dayTimezone();
      const fetched = await Promise.all(
        raceKeys.map(async (race) => {
          const url = `${apiEndpoints.app.data}/prestart-summary?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&date=${encodeURIComponent(date)}&race=${encodeURIComponent(race)}&view=${viewKey}`;
          const result = await getData(url);
          return { race, result };
        })
      );
      if (!isCurrentTableFetch(generation)) return;
      const matched: { race: string; row: Record<string, unknown> }[] = [];
      for (const item of fetched) {
        const apiRows = item.result?.success && item.result?.data?.rows
          ? (item.result.data.rows as Record<string, unknown>[])
          : [];
        const row = apiRows.find((candidate) => getTeamFromRow(candidate).toLowerCase() === sourceKey);
        if (row) matched.push({ race: item.race, row });
      }
      matched.sort(
        (a, b) =>
          (parseTimeToMs(a.row.race_start_time) ?? Number.POSITIVE_INFINITY) -
          (parseTimeToMs(b.row.race_start_time) ?? Number.POSITIVE_INFINITY)
      );
      const labels = disambiguateStartLabels(
        matched.map((item) => ({
          label: formatStartLabel(item.row.race_start_time, tz) || `Race ${item.race}`,
          race: item.race,
        }))
      );
      const rows: Record<string, unknown>[] = matched.map((item, index) => ({
        ...item.row,
        [START_COLUMN]: labels[index],
        race_key: item.race,
      }));
      rows.sort((a, b) => (parseTimeToMs(a.race_start_time) ?? 0) - (parseTimeToMs(b.race_start_time) ?? 0));
      if (!isCurrentTableFetch(generation)) return;
      setTableRows(rows);
      if (rows.length === 0) {
        setTableColumns([]);
        applyPendingOrClearSelection([]);
        setCourseAxisFromApi(undefined);
        return;
      }
      setTableColumns(columnsFromRows(rows));
      const first = rows[0];
      const fromRow = first?.Course_axis ?? first?.course_axis;
      const numFromRow = typeof fromRow === "number" && Number.isFinite(fromRow) ? fromRow : null;
      const raceForAxis = first?.race_key != null ? String(first.race_key) : "";
      if (numFromRow == null && raceForAxis) {
        try {
          const axisUrl = `${apiEndpoints.app.data}/prestart-summary?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&date=${encodeURIComponent(date)}&race=${encodeURIComponent(raceForAxis)}&view=courseaxis`;
          const axisResult = await getData(axisUrl);
          if (!isCurrentTableFetch(generation)) return;
          const axisRows = axisResult?.success && axisResult?.data?.rows ? (axisResult.data.rows as Record<string, unknown>[]) : [];
          const axisVal = axisRows[0]?.Course_axis ?? axisRows[0]?.course_axis;
          setCourseAxisFromApi(typeof axisVal === "number" && Number.isFinite(axisVal) ? axisVal : undefined);
        } catch {
          if (isCurrentTableFetch(generation)) setCourseAxisFromApi(undefined);
        }
      } else if (isCurrentTableFetch(generation)) {
        setCourseAxisFromApi(undefined);
      }
      if (!isCurrentTableFetch(generation)) return;
      applyPendingOrClearSelection(rows);
    } catch (err) {
      logError("Prestarts: fetch table failed", err);
      if (!isCurrentTableFetch(generation)) return;
      setTableRows([]);
      setTableColumns([]);
      applyPendingOrClearSelection([]);
      setCourseAxisFromApi(undefined);
    }
  };

  /** Resolve Show-me team names to the casing used in the table, then select; otherwise clear. */
  const applyPendingOrClearSelection = (rows: Record<string, unknown>[]) => {
    const pending = pendingShowMeTeams;
    pendingShowMeTeams = null;
    if (!pending || pending.length === 0) {
      setSelectedSourceNamesSignal([]);
      return;
    }
    const wanted = new Set(pending.map((t) => t.trim().toLowerCase()).filter(Boolean));
    const matched: string[] = [];
    for (const row of rows) {
      const team = seriesKeyFromRow(row);
      if (team && wanted.has(team.toLowerCase())) {
        matched.push(team);
      }
    }
    // Fall back to requested names if the table has no TEAM column yet
    setSelectedSourceNamesSignal(matched.length > 0 ? matched : pending.map((t) => t.trim()).filter(Boolean));
  };

  const scrollToPrestartEvidence = () => {
    // Prefer the map section when present; fall back to the table+map row.
    scrollToEvidence(".prestart-page .prestart-map-section, .prestart-page .prestart-table-map-row");
  };

  /** Dataset tracks are labeled by start time so each prestart is its own series. */
  const seriesLabelForEvent = (eventId: number, fallback: string): string => {
    if (!isDatasetMode()) return fallback;
    const row = tableRows().find((candidate) => Number(candidate.event_id) === Number(eventId));
    const start = row ? getRowValueByKey(row, START_COLUMN) : undefined;
    if (start != null && String(start).trim() !== "") return String(start).trim();
    return fallback;
  };

  const fetchMapData = async () => {
    const ids = eventIds();
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const desc = `${viewIndex()}_prestart`;
    if (!className || !projectId || ids.length === 0) {
      setMapData([]);
      return;
    }
    try {
      const url = `${apiEndpoints.app.data}/prestart-mapdata?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&event_list=${encodeURIComponent(JSON.stringify(ids))}&desc=${encodeURIComponent(desc)}`;
      const result = await getData(url);
      const raw = result?.success ? result.data : null;
      const rows: { event_id: number; source_name: string; json?: string | { values?: { lat?: string; lng?: string; time?: string; Time?: string; hdg?: string; Hdg?: string }[] }; JSON?: unknown }[] = Array.isArray(raw)
        ? raw
        : Array.isArray((raw as { rows?: unknown[] })?.rows)
          ? (raw as { rows: typeof rows }).rows
          : [];
      let tracks = rows.map((row) => {
        let json: string | { values?: { lat?: string; lng?: string; time?: string; Time?: string; hdg?: string; Hdg?: string }[]; marks?: { NAME?: string; LAT?: number; LON?: number; lat?: number; lon?: number; lng?: number }[] } | undefined = (row.json ?? (row as { JSON?: unknown }).JSON) as string | { values?: { lat?: string; lng?: string; time?: string; Time?: string; hdg?: string; Hdg?: string }[]; marks?: { NAME?: string; LAT?: number; LON?: number; lat?: number; lon?: number; lng?: number }[] } | undefined;
        if (typeof json === "string") {
          try {
            json = JSON.parse(json) as { values?: { lat?: string; lng?: string; time?: string; Time?: string; hdg?: string; Hdg?: string }[]; marks?: { NAME?: string; LAT?: number; LON?: number; lat?: number; lon?: number; lng?: number }[] };
          } catch {
            json = undefined;
          }
        }
        const values = (json && typeof json === "object" && Array.isArray(json.values)) ? json.values : [];
        const points = values
          .map((v) => {
            const vv = v as { lat?: string; lng?: string; Lat?: string; Lng?: string; time?: string; Time?: string; hdg?: string; Hdg?: string; note?: string; Note?: string };
            const lat = vv.lat ?? vv.Lat;
            const lng = vv.lng ?? vv.Lng;
            const timeRaw = vv.time ?? vv.Time;
            const hdgRaw = vv.hdg ?? vv.Hdg;
            const noteRaw = vv.note ?? vv.Note;
            const latN = parseFloat(String(lat));
            const lngN = parseFloat(String(lng));
            const timeN = timeRaw != null && timeRaw !== "" ? parseFloat(String(timeRaw)) : undefined;
            const hdgN = hdgRaw != null && hdgRaw !== "" ? parseFloat(String(hdgRaw)) : undefined;
            const noteStr = typeof noteRaw === "string" && noteRaw.trim() !== "" ? noteRaw.trim() : undefined;
            return {
              lat: latN,
              lng: lngN,
              ...(typeof timeN === "number" && Number.isFinite(timeN) ? { time: timeN } : {}),
              ...(typeof hdgN === "number" && Number.isFinite(hdgN) ? { hdg: hdgN } : {}),
              ...(noteStr !== undefined ? { note: noteStr } : {}),
            };
          })
          .filter((p) => !Number.isNaN(p.lat) && !Number.isNaN(p.lng) && Number.isFinite(p.lat) && Number.isFinite(p.lng));
        let marks: PrestartMark[] | undefined;
        const rawMarks = (json && typeof json === "object" && Array.isArray((json as { marks?: unknown }).marks)) ? (json as { marks: { NAME?: string; LAT?: number; LON?: number; lat?: number; lon?: number; lng?: number }[] }).marks : undefined;
        if (rawMarks && rawMarks.length > 0) {
          marks = rawMarks
            .map((m) => {
              const name = (m?.NAME ?? "").toString().trim().toUpperCase();
              if (!name) return null;
              const lat = Number(m?.LAT ?? (m as { lat?: number }).lat);
              const lon = Number(m?.LON ?? (m as { lon?: number }).lon ?? (m as { lng?: number }).lng);
              if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
              return { NAME: name, LAT: lat, LON: lon } as PrestartMark;
            })
            .filter((m): m is PrestartMark => m != null);
          if (marks.length === 0) marks = undefined;
        }
        return { event_id: row.event_id, source_name: (row as { source_name?: string }).source_name || "", points, ...(marks && marks.length > 0 ? { marks } : {}) };
      });

      if (isDatasetMode()) {
        tracks = tracks.map((track) => ({
          ...track,
          source_name: seriesLabelForEvent(track.event_id, track.source_name),
        }));
      } else {
        // Filter tracks by global source selection from filterStore (same as FleetMap)
        const fleetSources = getSelectedSourceNames();
        if (fleetSources) {
          tracks = tracks.filter((track) =>
            track.source_name && fleetSources.has(track.source_name.toLowerCase().trim())
          );
          logDebug('Prestart: Filtered map tracks by global source selection', {
            totalTracks: rows.length,
            filteredTracks: tracks.length,
            selectedSourceNames: Array.from(fleetSources)
          });
        }
      }

      const pointCounts = tracks.map((t) => t.points.length);
      logDebug("Prestart: mapdata", { desc, rows: rows.length, tracks: tracks.length, pointsPerTrack: pointCounts.join(","), totalPoints: pointCounts.reduce((a, b) => a + b, 0) });
      setMapData(tracks);
    } catch (err) {
      logError("Prestart: fetch mapdata failed", err);
      setMapData([]);
    }
  };

  const fetchTimeseries = async () => {
    const ids = eventIds();
    const className = selectedClassName();
    const projectId = selectedProjectId();
    if (!className || !projectId || ids.length === 0) {
      setTsData([]);
      return;
    }
    const idx = viewIndex();
    const desc = idx === 0 ? "0_Basics" : `${idx}_Details`;
    try {
      const url = `${apiEndpoints.app.data}/prestart-timeseries?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&event_list=${encodeURIComponent(JSON.stringify(ids))}&desc=${encodeURIComponent(desc)}`;
      const result = await getData(url);
      if (!result?.success || !Array.isArray(result.data)) {
        setTsData([]);
        return;
      }
      const rows = result.data as { event_id: number; source_name: string; json: string | { charts?: string[]; values?: Record<string, number>[] } }[];
      let parsed = rows.map((row) => {
        let J = row.json;
        if (typeof J === "string") {
          try {
            J = JSON.parse(J) as { charts?: string[]; values?: Record<string, number>[] };
          } catch {
            J = {};
          }
        }
        const charts = (J as { charts?: string[] }).charts || [];
        const values = (J as { values?: Record<string, number>[] }).values || [];
        return { event_id: row.event_id, source_name: row.source_name || "", charts, values };
      });

      if (isDatasetMode()) {
        parsed = parsed.map((row) => ({
          ...row,
          source_name: seriesLabelForEvent(row.event_id, row.source_name),
        }));
      } else {
        // Filter timeseries by global source selection from filterStore (same as FleetMap)
        const fleetSources = getSelectedSourceNames();
        if (fleetSources) {
          parsed = parsed.filter((ts) =>
            ts.source_name && fleetSources.has(ts.source_name.toLowerCase().trim())
          );
          logDebug('Prestart: Filtered timeseries by global source selection', {
            totalTimeseries: rows.length,
            filteredTimeseries: parsed.length,
            selectedSourceNames: Array.from(fleetSources)
          });
        }
      }

      setTsData(parsed);
    } catch (err) {
      logError("Prestart: fetch timeseries failed", err);
      setTsData([]);
    }
  };

  const getRowValue = (row: Record<string, unknown>, key: string): number | string | null => {
    const v = getRowValueByKey(row, key);
    if (v == null || v === "") return null;
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
    return String(v);
  };

  const valueKeys = createMemo(() => {
    const cols = tableColumns();
    return cols.filter(
      (k) =>
        k.toUpperCase() !== "RANK" &&
        !TEAM_COLUMN_NAMES.some((t) => t.toLowerCase() === k.toLowerCase()) &&
        k.toLowerCase() !== START_COLUMN.toLowerCase()
    );
  });

  const sortAndFormat = (key: string) => {
    const rows = tableRows();
    const current = sortConfig();
    const direction = current.key === key && current.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
    if (valueKeys().includes(key)) {
      const getVal = (row: Record<string, unknown>) => getRowValue(row, key);
      const values = rows.map((r) => getVal(r)).filter((v): v is number => typeof v === "number" && !Number.isNaN(v));
      const min = d3.min(values) ?? 0;
      const max = d3.max(values) ?? 0;
      setColumnScales((prev) => ({ ...prev, [key]: JSON.stringify({ domain: [min, max], range: [0, 6] }) }));
      setFormattedColumns((prev) => (prev.includes(key) ? prev : [...prev, key]));
    }
  };

  const clearFormatting = () => {
    setFormattedColumns([]);
    setColumnScales({});
    setSortConfig({ key: "", direction: "asc" });
  };

  const addAllFormatting = (): void => {
    const rows = tableRows();
    const keys = valueKeys();
    if (rows.length === 0 || keys.length === 0) return;
    const next: Record<string, string> = {};
    for (const key of keys) {
      const getVal = (row: Record<string, unknown>) => getRowValue(row, key);
      const values = rows.map((r) => getVal(r)).filter((v): v is number => typeof v === "number" && !Number.isNaN(v));
      const min = d3.min(values) ?? 0;
      const max = d3.max(values) ?? 0;
      next[key] = JSON.stringify({ domain: [min, max], range: [0, 6] });
    }
    setColumnScales(next);
    setFormattedColumns([...keys]);
  };

  const toggleFormatting = (): void => {
    if (formattedColumns().length === 0) {
      addAllFormatting();
    } else {
      clearFormatting();
    }
  };

  const sortedRows = createMemo(() => {
    const rows = tableRows();
    const { key, direction } = sortConfig();
    const currentView = view();

    if (rows.length === 0) return rows;

    if (isDatasetMode() && !key) {
      return [...rows].sort(
        (a, b) => (parseTimeToMs(a.race_start_time) ?? 0) - (parseTimeToMs(b.race_start_time) ?? 0)
      );
    }

    // For REACH and LEG 1 views, always sort with -999 values at the bottom
    const isReachOrLeg1View = currentView === "REACH" || currentView === "LEG 1";

    if (isReachOrLeg1View) {
      const rankKey = currentView === "LEG 1" ? "Leg1_dist" : "Reach_dist";

      return [...rows].sort((a, b) => {
        // First, check if either row has -999 in the ranking column (push to bottom)
        const aRankVal = getRowValue(a, rankKey);
        const bRankVal = getRowValue(b, rankKey);
        const aIs999 = typeof aRankVal === "number" && aRankVal === -999;
        const bIs999 = typeof bRankVal === "number" && bRankVal === -999;

        // If one has -999 and the other doesn't, push -999 to bottom
        if (aIs999 && !bIs999) return 1;
        if (!aIs999 && bIs999) return -1;

        // If no sort key is set, rank by distance descending (max = first)
        if (!key) {
          const aNum = typeof aRankVal === "number" ? aRankVal : Number(aRankVal);
          const bNum = typeof bRankVal === "number" ? bRankVal : Number(bRankVal);
          if (Number.isFinite(aNum) && Number.isFinite(bNum)) return bNum - aNum;
          return 0;
        }

        // Otherwise, apply normal sorting by the selected column
        const aV = getRowValue(a, key);
        const bV = getRowValue(b, key);

        if (aV == null && bV == null) return 0;
        if (aV == null) return direction === "asc" ? 1 : -1;
        if (bV == null) return direction === "asc" ? -1 : 1;

        const cmp = typeof aV === "number" && typeof bV === "number" ? aV - bV : String(aV).localeCompare(String(bV));
        return direction === "asc" ? cmp : -cmp;
      });
    }

    // For other views, use normal sorting
    if (!key) return rows;

    const getVal = (row: Record<string, unknown>) => getRowValue(row, key);
    return [...rows].sort((a, b) => {
      const aV = getVal(a);
      const bV = getVal(b);

      if (aV == null && bV == null) return 0;
      if (aV == null) return direction === "asc" ? 1 : -1;
      if (bV == null) return direction === "asc" ? -1 : 1;

      const cmp = typeof aV === "number" && typeof bV === "number" ? aV - bV : String(aV).localeCompare(String(bV));
      return direction === "asc" ? cmp : -cmp;
    });
  });

  const cellClassForScale = (
    row: Record<string, unknown>,
    key: string,
    scales: Record<string, string>,
    formatted: string[]
  ): string => {
    if (!formatted.includes(key) || !scales[key]) return "";
    try {
      const { domain, range } = JSON.parse(scales[key]) as { domain: number[]; range: number[] };
      const scale = d3.scaleLinear().domain(domain).range(range);
      const v = getRowValue(row, key);
      if (v == null || typeof v !== "number" || Number.isNaN(v)) return "";
      const x = scale(v);
      return `c${Math.round(Math.max(0, Math.min(6, x)))}`;
    } catch {
      return "";
    }
  };

  const formatCell = (v: unknown): string => {
    if (v == null || v === "") return "—";
    const n = Number(v);
    if (!Number.isNaN(n)) return n.toFixed(1);
    return String(v);
  };

  /** Copy prestart table to clipboard (values only). */
  const copyPrestartTableToClipboard = async () => {
    try {
      const viewKey = getTableViewKey(view());
      const cols = tableColumns();
      const rowsList = sortedRows();
      if (cols.length === 0) return;
      const headers = cols.map((col) => getColumnDisplay(viewKey, col).header);
      let clipboardText = headers.join("\t") + "\n";
      for (const row of rowsList) {
        const cells = cols.map((col) => {
          const cellValue = getRowValueByKey(row as Record<string, unknown>, col);
          const isSourceCol = isTeamColumnName(col);
          return isSourceCol ? String(cellValue ?? "—") : formatCell(cellValue);
        });
        clipboardText += cells.join("\t") + "\n";
      }
      log("Prestart: copy table to clipboard", { textLength: clipboardText.length });
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(clipboardText);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } else {
        const ta = document.createElement("textarea");
        ta.value = clipboardText;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      }
    } catch (err: unknown) {
      logError("Prestart: copy table failed", err);
    }
  };

  /** Sync selected source names to selectionStore as selectedGroupKeys (sources = groups). */
  createEffect(() => {
    const names = selectedSourceNamesSignal();
    setSelectedGroupKeys(names);
  });

  /** When SelectionBanner Clear is clicked, clear local selection so table and map update. */
  createEffect(() => {
    const handler = () => setSelectedSourceNamesSignal([]);
    window.addEventListener("selection-banner-cleared", handler);
    onCleanup(() => window.removeEventListener("selection-banner-cleared", handler));
  });

  const toggleSourceSelection = (sourceName: string) => {
    const key = String(sourceName ?? "").trim();
    if (!key) return;
    setSelectedSourceNamesSignal((prev) =>
      prev.includes(key) ? prev.filter((n) => n !== key) : [...prev, key]
    );
  };

  const canRemovePrestarts = () =>
    isDatasetMode() && hasProjectWritePermission(user(), Number(selectedProjectId()));

  const [removeMenu, setRemoveMenu] = createSignal<{ x: number; y: number; eventIds: number[] } | null>(null);
  const [removingPrestart, setRemovingPrestart] = createSignal(false);

  const eventIdFromRow = (row: Record<string, unknown>): number | null => {
    const raw = row.event_id;
    const id = typeof raw === "number" ? raw : Number(raw);
    return Number.isInteger(id) ? id : null;
  };

  const openRemoveMenu = (event: MouseEvent, seriesKey: string) => {
    if (!canRemovePrestarts()) return;
    const key = String(seriesKey ?? "").trim();
    if (!key) return;
    event.preventDefault();
    event.stopPropagation();
    let keys = selectedSourceNamesSignal();
    if (!keys.includes(key)) {
      keys = [key];
      setSelectedSourceNamesSignal(keys);
    }
    const ids = tableRows()
      .filter((row) => keys.includes(seriesKeyFromRow(row)))
      .map(eventIdFromRow)
      .filter((id): id is number => id != null);
    if (ids.length === 0) {
      logDebug("Prestarts: selected row has no event id", { key });
      return;
    }
    setRemoveMenu({ x: event.clientX, y: event.clientY, eventIds: ids });
  };

  const removeSelectedPrestarts = async () => {
    const menu = removeMenu();
    if (!menu || removingPrestart()) return;
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const datasetId = Number(selectedDatasetId());
    setRemoveMenu(null);
    if (!className || projectId == null || !Number.isInteger(datasetId)) {
      toastStore.showToast("error", "Could not remove the prestart");
      return;
    }
    const count = menu.eventIds.length;
    const label = count === 1 ? "this prestart" : `${count} prestarts`;
    if (!window.confirm(`Remove ${label}? This deletes the prestart from the dataset.`)) return;
    setRemovingPrestart(true);
    try {
      const result = await deleteData(`${apiEndpoints.admin.events}`, {
        class_name: className,
        project_id: projectId,
        dataset_id: datasetId,
        events: menu.eventIds,
      });
      if (!result?.success) {
        logError("Prestarts: remove failed", result?.message ?? result);
        toastStore.showToast("error", "Could not remove the prestart", result?.message);
        return;
      }
      setSelectedSourceNamesSignal([]);
      await fetchRaces();
      const generation = ++tableFetchGeneration;
      await fetchDatasetTable(generation);
      toastStore.showToast("success", count === 1 ? "Prestart removed" : `${count} prestarts removed`);
    } catch (err) {
      logError("Prestarts: remove failed", err);
      toastStore.showToast("error", "Could not remove the prestart");
    } finally {
      setRemovingPrestart(false);
    }
  };

  createEffect(() => {
    if (!removeMenu()) return;
    const close = () => setRemoveMenu(null);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("click", close);
    document.addEventListener("keydown", onKey);
    onCleanup(() => {
      document.removeEventListener("click", close);
      document.removeEventListener("keydown", onKey);
    });
  });

  const filterInsightRows = (rows: Record<string, unknown>[]): Record<string, unknown>[] => {
    const selected = getSelectedSourceNames();
    if (!selected) return rows;
    return rows.filter((row) => {
      const sourceName = getTeamFromRow(row);
      return sourceName && selected.has(sourceName.toLowerCase().trim());
    });
  };

  const fetchPrestartViewRows = async (
    viewKey: string,
    race: string
  ): Promise<Record<string, unknown>[]> => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const date = activeDate();
    if (!className || !projectId || date.length !== 8 || !race) return [];
    const url = `${apiEndpoints.app.data}/prestart-summary?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&date=${encodeURIComponent(date)}&race=${encodeURIComponent(race)}&view=${encodeURIComponent(viewKey)}`;
    const result = await getData(url);
    if (!result?.success || !result?.data?.rows) return [];
    return filterInsightRows(result.data.rows as Record<string, unknown>[]);
  };

  const fetchInsightBundle = async (race: string): Promise<StartViewsBundle> => {
    const [prestart, acceleration, maxbsp, reach, leg1] = await Promise.all([
      fetchPrestartViewRows("prestart", race),
      fetchPrestartViewRows("acceleration", race),
      fetchPrestartViewRows("maxbsp", race),
      fetchPrestartViewRows("reach", race),
      fetchPrestartViewRows("leg1", race),
    ]);
    return { prestart, acceleration, maxbsp, reach, leg1, raceKey: race };
  };

  const refreshInsightFindings = async () => {
    const race = selectedRace();
    const home = resolveHomeBoatName();
    if (!race || !activeDate()) {
      setInsightFindings([]);
      setInsightFleetSize(0);
      setInsightStatus('');
      return;
    }
    try {
      const bundle = await fetchInsightBundle(race);
      setInsightFleetSize(bundle.prestart.length || bundle.leg1.length);

      if (!home) {
        setInsightFindings([]);
        setInsightStatus('');
        return;
      }

      if (!homeInStartBundle(bundle, home)) {
        setInsightFindings([]);
        setInsightStatus(`${home} is not in this race.`);
        return;
      }
      setInsightStatus('');

      const raceFindings = buildStartFindingsForRace(bundle, home);

      // Day trends when multiple races exist: fetch lighter bundles for each race
      const raceKeys = races();
      let trends: Finding[] = [];
      if (raceKeys.length >= 2) {
        const perRace = await Promise.all(
          raceKeys.map(async (rk) => {
            if (rk === race) return bundle;
            const [prestart, acceleration, leg1] = await Promise.all([
              fetchPrestartViewRows("prestart", rk),
              fetchPrestartViewRows("acceleration", rk),
              fetchPrestartViewRows("leg1", rk),
            ]);
            return {
              prestart,
              acceleration,
              maxbsp: [],
              reach: [],
              leg1,
              raceKey: rk,
            } satisfies StartViewsBundle;
          })
        );
        trends = buildStartDayTrendFindings(perRace, home);
      }

      setInsightFindings(sortFindings([...trends, ...raceFindings]).slice(0, 12));
    } catch (err) {
      logError("Prestart: insight findings failed", err);
      setInsightFindings([]);
      setInsightStatus('');
    }
  };

  const handleInsightShowMe = (action: ShowMeAction) => {
    const teams = (action.highlightTeams ?? [])
      .map((t) => String(t).trim())
      .filter(Boolean);

    // Clear immediately, then re-select after the (possibly view-triggered) table fetch.
    setSelectedSourceNamesSignal([]);
    pendingShowMeTeams = teams.length > 0 ? teams : null;
    pendingScrollToEvidence = true;

    const nextView = action.view
      ? VIEW_OPTIONS.find((v) => v.toUpperCase() === String(action.view).toUpperCase())
      : undefined;
    const viewChanging = nextView != null && nextView !== view();

    if (nextView) {
      setView(nextView);
    }

    if (!viewChanging) {
      // Same view: fetchTable will not re-run from the view effect — apply now.
      applyPendingOrClearSelection(tableRows());
      pendingScrollToEvidence = false;
      scrollToPrestartEvidence();
    }
    // View change: scroll after loading remounts .prestart-table-map-row (see fetch effect).
  };

  const isSourceSelected = createSelector(
    selectedSourceNamesSignal,
    (sourceName: string, selected: string[]) => {
      const key = String(sourceName ?? "").trim().toLowerCase();
      if (!key) return false;
      return selected.some((n) => n.trim().toLowerCase() === key);
    }
  );

  /** Star on map: in REACH/LEG 1 = time from map point with note 'max bsp'; in ACCELERATION = Time_accmax; in other views = Time_bspmax (max BSP). */
  const markerTimeBySource = createMemo((): Record<string, number> => {
    const v = view();
    const selected = selectedSourceNamesSignal();
    if (selected.length === 0) return {};
    const out: Record<string, number> = {};
    if (v === "REACH" || v === "LEG 1") {
      const tracks = mapData();
      for (const track of tracks) {
        if (!selected.includes(track.source_name)) continue;
        const pt = track.points.find((p) => p.note === "max bsp" && typeof p.time === "number" && Number.isFinite(p.time));
        if (pt != null) out[track.source_name] = pt.time as number;
      }
      return out;
    }
    const rows = tableRows();
    const timeKey = v === "ACCELERATION" ? "Time_accmax" : "Time_bspmax";
    for (const row of rows) {
      const sourceName = seriesKeyFromRow(row as Record<string, unknown>);
      if (!selected.includes(sourceName)) continue;
      const val = getRowValue(row as Record<string, unknown>, timeKey);
      if (val != null && typeof val === "number" && Number.isFinite(val) && val > -999) {
        out[sourceName] = val;
      }
    }
    return out;
  });

  /** Golden boat on map: only when NOT in ACCELERATION or MAX BSP view. In REACH/LEG 1 = time from map point with note 'max accel'; otherwise Time_accmax. */
  const markerTimeBySourceBoat = createMemo((): Record<string, number> => {
    const v = view();
    if (v === "ACCELERATION" || v === "MAX BSP") return {};
    const selected = selectedSourceNamesSignal();
    if (selected.length === 0) return {};
    const out: Record<string, number> = {};
    if (v === "REACH" || v === "LEG 1") {
      const tracks = mapData();
      for (const track of tracks) {
        if (!selected.includes(track.source_name)) continue;
        const pt = track.points.find((p) => p.note === "max accel" && typeof p.time === "number" && Number.isFinite(p.time));
        if (pt != null) out[track.source_name] = pt.time as number;
      }
      return out;
    }
    const rows = tableRows();
    for (const row of rows) {
      const sourceName = seriesKeyFromRow(row as Record<string, unknown>);
      if (!selected.includes(sourceName)) continue;
      const val = getRowValue(row as Record<string, unknown>, "Time_accmax");
      if (val != null && typeof val === "number" && Number.isFinite(val) && val > -999) {
        out[sourceName] = val;
      }
    }
    return out;
  });

  /** Same as Race Summary: show copy control only after hover ≥ 2s to avoid accidental clicks */
  createEffect(() => {
    if (isHoveredTable()) {
      const timer = setTimeout(() => setShowCopyTable(true), 2000);
      onCleanup(() => clearTimeout(timer));
    } else {
      setShowCopyTable(false);
    }
  });

  onMount(() => {
    logDebug("Prestart: mount - setting up scaling");
    logPageLoad(isDatasetMode() ? "Prestarts.tsx" : "Prestart.tsx", pageLogName(), "Loaded");
    const cleanupScaling = setupMediaContainerScaling({
      logPrefix: "Prestart",
      scaleToWidth: true,
    });

    // Match SelectionBanner: x clears, c cuts (same as Fleet Maneuvers / Performance).
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
        return;
      }
      if (document.querySelector(".pagesettings-modal")) {
        return;
      }
      if (event.key === "x" || event.key === "X") {
        event.preventDefault();
        pendingShowMeTeams = null;
        runSelectionBannerClear();
        setSelectedSourceNamesSignal([]);
        logDebug("Prestart: Cleared selection (x key)");
        return;
      }
      if (event.key === "c" || event.key === "C") {
        event.preventDefault();
        runSelectionBannerCut();
        logDebug("Prestart: Cut selection (c key)");
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    onCleanup(() => {
      logDebug("Prestart: cleanup - removing scaling");
      cleanupScaling();
      window.removeEventListener("keydown", handleKeyDown);
    });
  });

  createEffect(() => {
    if (!isDatasetMode()) {
      setDatasetReportDate("");
      setDatasetReportSource("");
      setDatasetInfoLoaded(false);
      return;
    }
    const datasetId = selectedDatasetId();
    const className = selectedClassName();
    const projectId = selectedProjectId();
    if (!datasetId || !className || !projectId) {
      setDatasetReportDate("");
      setDatasetReportSource("");
      setDatasetInfoLoaded(false);
      return;
    }
    let cancelled = false;
    onCleanup(() => {
      cancelled = true;
    });
    setDatasetInfoLoaded(false);
    void (async () => {
      try {
        const response = await getData(
          `${apiEndpoints.app.datasets}/info?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(String(projectId))}&dataset_id=${encodeURIComponent(String(datasetId))}`
        );
        if (cancelled) return;
        const rawDate = response?.success ? String(response.data?.date ?? "") : "";
        const sourceName = response?.success ? String(response.data?.source_name ?? "").trim() : "";
        const date = rawDate.replace(/[-/]/g, "");
        if (date.length === 8 && sourceName) {
          setDatasetReportDate(date);
          setDatasetReportSource(sourceName);
        } else {
          setDatasetReportDate("");
          setDatasetReportSource("");
          logDebug("Prestarts: dataset info missing date or source", { datasetId, rawDate, sourceName });
        }
      } catch (err) {
        if (!cancelled) {
          logError("Prestarts: dataset info failed", err);
          setDatasetReportDate("");
          setDatasetReportSource("");
        }
      } finally {
        if (!cancelled) setDatasetInfoLoaded(true);
      }
    })();
  });

  createEffect(() => {
    selectedClassName();
    selectedProjectId();
    if (isDatasetMode()) {
      selectedDatasetId();
      datasetReportDate();
    } else {
      selectedDate();
    }
    void fetchRaces();
  });

  createEffect(() => {
    view();
    const datasetMode = isDatasetMode();
    if (datasetMode) {
      selectedDatasetId();
      datasetReportDate();
      datasetReportSource();
      if (!datasetInfoLoaded()) {
        setLoading(true);
        return;
      }
      const raceList = races();
      if (!activeDate() || raceList.length === 0) {
        setLoading(false);
        setTableRows([]);
        setTableColumns([]);
        return;
      }
    } else {
      filterStoreSelectedSources();
      if (!selectedRace() || !activeDate()) {
        setLoading(false);
        return;
      }
    }
    if (pendingShareTimeSec === undefined) {
      setSelectedTimeSec(null);
    }
    const generation = ++tableFetchGeneration;
    setLoading(true);
    void (async () => {
      try {
        if (datasetMode) await fetchDatasetTable(generation);
        else await fetchTable(generation);
      } finally {
        if (generation !== tableFetchGeneration) return;
        setLoading(false);
        if (pendingShareTimeSec !== undefined) {
          setSelectedTimeSec(pendingShareTimeSec);
          pendingShareTimeSec = undefined;
        }
        if (pendingScrollToEvidence) {
          pendingScrollToEvidence = false;
          // Defer until <Show when={!loading()}> remounts the table/map.
          requestAnimationFrame(() => scrollToPrestartEvidence());
        }
      }
    })();
  });

  // Insight cards: recompute when race, fleet filter, or home boat changes (not on view switch).
  createEffect(() => {
    if (isDatasetMode()) {
      setInsightFindings([]);
      setInsightFleetSize(0);
      setInsightStatus("");
      return;
    }
    selectedRace();
    races();
    filterStoreSelectedSources();
    persistantStore.homeBoatName();
    activeDate();
    void refreshInsightFindings();
  });

  const fetchMarks = async () => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const date = activeDate();
    if (!className || !projectId || date.length !== 8) {
      setMarksByTime([]);
      return;
    }
    const dateStr = formatDateForMarksApi(date);
    try {
      for (const objName of ["markwind", "marks"] as const) {
        const url = `${apiEndpoints.app.projects}/object?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&date=${encodeURIComponent(dateStr)}&object_name=${objName}`;
        const result = await getData(url);
        const data = result?.success ? result.data : null;
        if (!data) continue;
        let arr: { DATETIME?: string; Datetime?: string; datetime?: string; TIMESTAMP?: string; MARKS?: { NAME?: string; POSITION?: string; LAT?: number; LON?: number; lng?: number }[] }[] = [];
        const jsonValue = (data as { value?: unknown })?.value ?? data;
        if (Array.isArray(jsonValue)) {
          arr = jsonValue;
        } else if (typeof jsonValue === "string") {
          arr = JSON.parse(jsonValue) as typeof arr;
        } else if (jsonValue && typeof jsonValue === "object" && !Array.isArray(jsonValue)) {
          const single = (jsonValue as { MARKS?: { NAME?: string; POSITION?: string; LAT?: number; LON?: number; lng?: number }[] }).MARKS;
          if (Array.isArray(single)) arr = [{ MARKS: single }];
          else arr = [];
        } else {
          arr = Array.isArray(jsonValue) ? jsonValue : [];
        }
        const entries: { datetime: string; marks: PrestartMark[] }[] = [];
        for (const entry of arr) {
          const mar = entry?.MARKS;
          const dt = entry?.DATETIME ?? entry?.Datetime ?? entry?.datetime ?? entry?.TIMESTAMP ?? "";
          if (!Array.isArray(mar)) continue;
          const out: PrestartMark[] = [];
          for (const mark of mar) {
            const name = (mark?.NAME ?? mark?.POSITION ?? "").toString().trim().toUpperCase();
            if (!PRESTART_MARK_NAMES.includes(name as (typeof PRESTART_MARK_NAMES)[number])) continue;
            const lat = Number(mark?.LAT ?? (mark as { lat?: number }).lat);
            const lon = Number(mark?.LON ?? (mark as { lon?: number }).lon ?? (mark as { lng?: number }).lng);
            if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
            out.push({ NAME: name, LAT: lat, LON: lon });
          }
          if (out.length > 0) entries.push({ datetime: String(dt), marks: out });
        }
        if (entries.length > 0) {
          setMarksByTime(entries);
          return;
        }
      }
      setMarksByTime([]);
    } catch (err) {
      logError("Prestart: fetch marks failed", err);
      setMarksByTime([]);
    }
  };

  /**
   * Marks for the current race: use same logic as fleet map (RaceCourseLayer findNearestByTime).
   * Prefer last entry at or before race_start_time; if none, use latest entry; else closest by time.
   */
  const marksForRace = createMemo(() => {
    const entries = marksByTime();
    const firstRow = tableRows()[0];
    const raceStart = firstRow?.race_start_time;
    if (entries.length === 0) return [];
    if (entries.length === 1) return entries[0].marks;
    const raceMs = parseTimeToMs(raceStart);
    if (raceMs == null) return entries[entries.length - 1].marks;
    const sorted = [...entries].sort((a, b) => {
      const ta = parseTimeToMs(a.datetime) ?? -Infinity;
      const tb = parseTimeToMs(b.datetime) ?? -Infinity;
      return ta - tb;
    });
    let lastAtOrBefore: typeof entries[0] | null = null;
    let lastAtOrBeforeMs = -Infinity;
    for (let i = sorted.length - 1; i >= 0; i--) {
      const entry = sorted[i];
      const entryMs = parseTimeToMs(entry.datetime);
      if (entryMs == null) continue;
      if (entryMs <= raceMs && entryMs > lastAtOrBeforeMs) {
        lastAtOrBefore = entry;
        lastAtOrBeforeMs = entryMs;
      }
    }
    if (lastAtOrBefore) return lastAtOrBefore.marks;
    const latest = sorted[sorted.length - 1];
    if (latest && parseTimeToMs(latest.datetime) != null) return latest.marks;
    let best = sorted[0];
    let bestDiff = Infinity;
    for (const entry of sorted) {
      const entryMs = parseTimeToMs(entry.datetime);
      if (entryMs == null) continue;
      const diff = Math.abs(entryMs - raceMs);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = entry;
      }
    }
    return best.marks;
  });

  /** Marks for the prestart map: use marks from first selected source's track (or first track with marks).
   * Embedded marks are rotated and transposed with the map data so they align with tracks. Fallback to marksForRace() for old data without embedded marks. */
  const marksForMap = createMemo((): { marks: PrestartMark[]; alreadyRotated: boolean } => {
    const tracks = mapData();
    const selected = isDatasetMode()
      ? new Set(selectedSourceNamesSignal().map((name) => name.toLowerCase().trim()).filter((name) => name !== ""))
      : getSelectedSourceNames();
    if (tracks.length === 0) return { marks: marksForRace(), alreadyRotated: false };
    const withMarks = tracks.filter((t): t is typeof t & { marks: PrestartMark[] } => Array.isArray(t.marks) && t.marks.length > 0);
    if (withMarks.length === 0) return { marks: marksForRace(), alreadyRotated: false };
    const firstSelected = selected && selected.size > 0
      ? withMarks.find((t) => t.source_name && selected.has(t.source_name.toLowerCase().trim()))
      : null;
    const chosen = firstSelected ?? withMarks[0];
    return { marks: chosen.marks, alreadyRotated: true };
  });

  createEffect(() => {
    selectedClassName();
    selectedProjectId();
    selectedDate();
    if (activeDate()) fetchMarks();
    else setMarksByTime([]);
  });

  createEffect(() => {
    view();
    if (!isDatasetMode()) filterStoreSelectedSources();
    const ids = eventIds();
    if (ids.length === 0) {
      setMapData([]);
      setTsData([]);
      return;
    }
    setMapData([]);
    setTsData([]);
    fetchMapData();
    fetchTimeseries();
  });

  /** Timeseries desc for current view: 0_Basics (PRESTART), 1_Details (ACCELERATION/MAX BSP/REACH), 2_Details (LEG 1 only). */
  const tsDesc = () => {
    const idx = viewIndex();
    return idx === 0 ? "0_Basics" : `${idx}_Details`;
  };

  const tsCharts = createMemo(() => {
    const data = tsData();
    const desc = tsDesc();
    const expectedCharts = PRESTART_TIMESERIES_CHARTS[desc];
    if (expectedCharts && expectedCharts.length > 0) return expectedCharts;
    if (data.length === 0) return [];
    const charts = data[0].charts || [];
    return charts.filter((c) => c && typeof c === "string");
  });

  /** True when report should be hidden and "data not available" message shown. Only when there are no races for the date; once races exist we show the UI (table may be empty or loading). */
  const hasNoData = createMemo(() => {
    if (loading()) return false;
    if (isDatasetMode() && !datasetInfoLoaded()) return false;
    const raceList = races();
    return raceList.length === 0;
  });

  const renderTable = createMemo(() => {
    const scales = columnScales();
    const formatted = formattedColumns();
    const cols = tableColumns();
    const rows = sortedRows();
    const viewKey = getTableViewKey(view());

    if (cols.length === 0) {
      return (
        <div class="prestart-timeseries-empty">
          {isDatasetMode() ? "No prestarts for this source on this day." : "Loading table..."}
        </div>
      );
    }

    // @ts-ignore - reactive dependency for selection
    const _selectedNames = selectedSourceNamesSignal();

    return (
      <table class="prestart-table">
        <thead>
          <tr>
            <For each={cols}>
              {(col) => {
                const { header, tooltip } = getColumnDisplay(viewKey, col);
                const thClass = "centered";
                return (
                  <th
                    class={thClass}
                    title={`${tooltip} — Click to sort`}
                    style={{ cursor: "pointer" }}
                    onClick={() => sortAndFormat(col)}
                  >
                    {header} {sortConfig().key === col ? (sortConfig().direction === "asc" ? "▲" : "▼") : ""}
                  </th>
                );
              }}
            </For>
          </tr>
        </thead>
        <tbody>
          <For each={rows}>
            {(row) => {
              const sourceName = seriesKeyFromRow(row as Record<string, unknown>);
              const selected = isSourceSelected(sourceName);
              const sourceColor = seriesColor(sourceName || undefined);
              const rowColor = selected ? (sourceColor ?? "var(--color-bg-secondary)") : undefined;
              /* Same as maneuvers: full-row selection (tr gets background; cells inherit via CSS). Use same style string with !important. */
              const rowStyle =
                selected && rowColor
                  ? `background-color: ${rowColor} !important; color: white !important; cursor: pointer`
                  : "cursor: pointer";
              return (
                <tr
                  class={selected ? "row-selected" : ""}
                  style={rowStyle}
                  onClick={() => sourceName && toggleSourceSelection(sourceName)}
                  onContextMenu={(event) => sourceName && openRemoveMenu(event, sourceName)}
                >
                  <For each={cols}>
                    {(col) => {
                      const scaleClass = cellClassForScale(row as Record<string, unknown>, col, scales, formatted);
                      const isSourceCol = isDatasetMode()
                        ? col.toLowerCase() === START_COLUMN.toLowerCase()
                        : isTeamColumnName(col);
                      const cellValue = getRowValueByKey(row as Record<string, unknown>, col);
                      const displayValue = isSourceCol ? String(cellValue ?? "—") : formatCell(cellValue);
                      const cellClass = ["centered", scaleClass].filter(Boolean).join(" ");
                      const cellStyle =
                        selected
                          ? (isSourceCol && sourceColor ? { "font-weight": "600" } : undefined)
                          : isSourceCol && sourceColor
                            ? { color: sourceColor, "font-weight": "600" }
                            : undefined;
                      return (
                        <td class={cellClass} style={cellStyle}>
                          {displayValue}
                        </td>
                      );
                    }}
                  </For>
                </tr>
              );
            }}
          </For>
        </tbody>
      </table>
    );
  });

  return (
    <div id="media-container" class="prestart-page">
      <div class="container">
        {/* Same scroll pattern as Race Summary: scroll container height set by setupMediaContainerScaling, scroll in inner */}
        <div class="performance-charts-scroll-container">
          <div class="performance-charts-scroll-inner" style={{ padding: "1.5rem", "box-sizing": "border-box" }}>
            <Show when={hasNoData()}>
              <div class="report-no-data-container">
                <p class="report-no-data-message">
                  {isDatasetMode() ? "No prestarts for this source on this day." : "Data is not available yet."}
                </p>
              </div>
            </Show>

            <Show when={!hasNoData()}>
              <div class="prestart-controls" style={{ "margin-bottom": "1rem", display: "flex", "flex-wrap": "wrap", "align-items": "center", gap: "0.5rem" }}>
                <Show when={!isDatasetMode()}>
                  <RaceSettings />
                </Show>
                <DropDownButton
                  options={[...VIEW_OPTIONS]}
                  defaultText={view()}
                  smallLabel="View"
                  size="big"
                  handleSelection={async (v) => {
                    setView(v as typeof VIEW_OPTIONS[number]);
                    const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
                    await logActivity(project_id, dataset_id, isDatasetMode() ? "Prestarts.tsx" : "Prestart.tsx", pageLogName(), `View changed to ${v}`);
                  }}
                />
                <Show when={!isDatasetMode()}>
                  <DropDownButton
                    options={races().length > 0 ? races() : ["Select race"]}
                    defaultText={selectedRace() || "Select race"}
                    smallLabel="Races"
                    size="big"
                    handleSelection={async (v) => {
                      if (v !== "Select race") {
                        setSelectedRace(v);
                        const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
                        await logActivity(project_id, dataset_id, "Prestart.tsx", "Start Summary", `Race changed to ${v}`);
                      }
                    }}
                  />
                </Show>
              </div>

              <div class="prestart-content-area" style={{ position: "relative", "min-height": "200px" }}>
                <Show when={loading()}>
                  <LoadingOverlay />
                </Show>

                <Show when={!loading()}>
                  <Show when={!isDatasetMode()}>
                    <InsightPanel
                      areaLabel="the start"
                      findings={insightFindings}
                      fleetSize={insightFleetSize}
                      statusMessage={insightStatus}
                      onShowMe={handleInsightShowMe}
                    />
                  </Show>
                  <div class="prestart-table-map-row">
                    {/* Table: same structure as Race Summary — data-table-container + scrollable-container for fixed columns, wrapping headers, overflow scroll */}
                    <section class="prestart-table-section" style={{ "margin-bottom": "1.5rem" }}>
                      <div
                        class="copy-table-hover-wrapper"
                        onMouseEnter={() => setIsHoveredTable(true)}
                        onMouseLeave={() => setIsHoveredTable(false)}
                      >
                        <div class="data-table-container">
                          <div class="scrollable-container">
                            {renderTable()}
                          </div>
                        </div>
                        {showCopyTable() && tableRows().length > 0 && (
                          <div class="copy-table-data-actions">
                            <button
                              type="button"
                              class="copy-table-data-btn"
                              classList={{ "copy-table-data-btn-success": copySuccess() }}
                              onClick={copyPrestartTableToClipboard}
                            >
                              {copySuccess() ? "✓ Copied!" : "Copy Table Data"}
                            </button>
                          </div>
                        )}
                        <div class="prestart-table-actions-row">
                          <p class="prestart-ranking-label">
                            {isDatasetMode() ? "Ordered by start time." : getRankingLabel(view())}
                          </p>
                          <button class="btn" type="button" onClick={toggleFormatting}>
                            {formattedColumns().length > 0 ? "Clear Formatting" : "Add formatting"}
                          </button>
                        </div>
                      </div>
                    </section>

                    {/* Map */}
                    <section class="prestart-map-section">
                      <div class="prestart-map-wrapper">
                        <PrestartReportMap
                          tracks={mapDataForView()}
                          marks={marksForMap().marks}
                          marksAlreadyRotated={marksForMap().alreadyRotated}
                          courseAxisDeg={(() => {
                            // Use first table row's Course_axis so marks use one rotation; backend uses race-level course axis when available for alignment
                            const r = tableRows()[0];
                            const fromRow = r?.Course_axis ?? (r as Record<string, unknown> | undefined)?.course_axis;
                            const n = typeof fromRow === "number" && Number.isFinite(fromRow) ? fromRow : courseAxisFromApi();
                            return typeof n === "number" && Number.isFinite(n) ? n : undefined;
                          })()}
                          selectedSourceNames={selectedSourceNames()}
                          selectedTimeSec={selectedTimeSec()}
                          markerTimeBySource={markerTimeBySource()}
                          markerTimeBySourceBoat={markerTimeBySourceBoat()}
                          onTrackClick={toggleSourceSelection}
                          onTrackContextMenu={(sourceName, event) => openRemoveMenu(event, sourceName)}
                          getSourceColor={seriesColor}
                        />
                        <Show when={mapBspRows().length > 0}>
                          <div class="prestart-map-bsp-panel">
                            <table class="prestart-map-bsp-table">
                              <caption class="prestart-map-bsp-caption">BSP (kph)</caption>
                              <thead>
                                <tr>
                                  <th scope="col">{isDatasetMode() ? "Start" : "Source"}</th>
                                  <th scope="col">BSP</th>
                                </tr>
                              </thead>
                              <tbody>
                                <For each={mapBspRows()}>
                                  {(row) => (
                                    <tr
                                      classList={{ "prestart-map-bsp-row-dimmed": row.dimmed }}
                                      onContextMenu={(event) => openRemoveMenu(event, row.sourceName)}
                                    >
                                      <td>
                                        <span class="prestart-map-bsp-source">
                                          <span class="prestart-map-bsp-swatch" style={{ "background-color": row.color }} />
                                          <span class="prestart-map-bsp-name" title={row.sourceName}>{row.sourceName}</span>
                                        </span>
                                      </td>
                                      <td class="prestart-map-bsp-value">{row.bspDisplay}</td>
                                    </tr>
                                  )}
                                </For>
                              </tbody>
                            </table>
                          </div>
                        </Show>
                        <Show when={mapTimeExtent()}>
                          {(extent) => {
                            const sliderTime = () => {
                              const [min, max] = extent();
                              const selected = selectedTimeSec();
                              if (selected == null || !Number.isFinite(selected)) {
                                return max;
                              }
                              return Math.min(max, Math.max(min, selected));
                            };
                            return (
                              <div class="prestart-map-time-slider">
                                <span class="prestart-map-time-slider-caption">{formatPrestartSliderTime(sliderTime())}</span>
                                <input
                                  class="prestart-map-time-range"
                                  type="range"
                                  min={extent()[0]}
                                  max={extent()[1]}
                                  step="any"
                                  value={sliderTime()}
                                  aria-label="Selected time"
                                  onInput={(event) => {
                                    const next = Number(event.currentTarget.value);
                                    if (Number.isFinite(next)) {
                                      setSelectedTimeSec(next);
                                    }
                                  }}
                                />
                              </div>
                            );
                          }}
                        </Show>
                      </div>
                    </section>
                  </div>

                  {/* Timeseries: full width below table+map row */}
                  <section class="prestart-timeseries-section">
                    <div class="prestart-timeseries-charts">
                      <Show when={tsCharts().length > 0} fallback={<p class="prestart-timeseries-empty">No timeseries data for this view. Run prestart processing to populate charts.</p>}>
                        <div class="prestart-timeseries-container">
                          <For each={tsCharts()}>
                            {(channelKey) => (
                              <div class="prestart-chart-wrap">
                                <PrestartChart
                                  channelKey={channelKey}
                                  tsData={tsDataForView()}
                                  selectedSourceNames={selectedSourceNames()}
                                  selectedTimeSec={selectedTimeSec()}
                                  onTimeSelect={(t) => setSelectedTimeSec(t)}
                                  onTimeClear={() => setSelectedTimeSec(null)}
                                  markerTimesBySource={markerTimeBySource()}
                                  onSourceClick={toggleSourceSelection}
                                  onSourceContextMenu={(sourceName, event) => openRemoveMenu(event, sourceName)}
                                  getColorForSource={seriesColor}
                                />
                              </div>
                            )}
                          </For>
                        </div>
                      </Show>
                    </div>
                  </section>
                </Show>
              </div>
            </Show>
          </div>
        </div>
        <Show when={removeMenu()}>
          {(menu) => (
            <Portal mount={typeof document !== "undefined" ? document.body : undefined}>
              <ul
                class="prestart-ctx-menu"
                style={{ left: `${menu().x}px`, top: `${menu().y}px` }}
              >
                <li>
                  <button
                    type="button"
                    class="prestart-ctx-menu-item"
                    disabled={removingPrestart()}
                    onClick={(event) => {
                      event.stopPropagation();
                      void removeSelectedPrestarts();
                    }}
                  >
                    {menu().eventIds.length === 1 ? "Remove prestart" : `Remove ${menu().eventIds.length} prestarts`}
                  </button>
                </li>
              </ul>
            </Portal>
          )}
        </Show>
        <Portal mount={typeof document !== "undefined" ? document.body : undefined}>
          <div
            id="prestart-timeseries-tooltip"
            class="tooltip"
            style={{
              opacity: tooltip().visible ? 1 : 0,
              left: `${tooltip().x}px`,
              top: `${tooltip().y}px`,
              position: "fixed",
              "pointer-events": "none",
              "z-index": 9999,
            }}
            innerHTML={sanitizeTooltipHtml(tooltip().content)}
          />
        </Portal>
      </div>
    </div>
  );
}
